import * as Sentry from "@sentry/react"

/**
 * Initialize Sentry. Skipped entirely on the `local` env (the local DSN is a
 * placeholder) and when no DSN is configured. Call once, as early as possible.
 */
export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const mode = import.meta.env.VITE_NODE_ENV
  const version = import.meta.env.VITE_APP_VERSION

  if (!dsn || mode === "local") {
    return
  }

  if (!["development", "staging", "production"].includes(mode)) {
    return
  }

  const isDev = ["development", "staging"].includes(mode)

  Sentry.init({
    dsn,
    sendDefaultPii: true,
    environment: mode,
    release: version,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllInputs: true
      })
    ],
    maxBreadcrumbs: 50,
    tracesSampleRate: isDev ? 1.0 : 0.1,
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [
      "timeout of",
      "AbortError: The operation was aborted.",
      "Failed to fetch dynamically imported module",
      "error loading dynamically imported module",
      "Importing a module script failed"
    ]
  })
}

export const setSentryUser = (
  userId: string | number,
  username?: string,
  additionalData?: Record<string, string>
) => {
  Sentry.setUser({ id: String(userId), username, ...additionalData })
}

export const clearSentryUser = () => {
  Sentry.setUser(null)
}

export const setSentryContext = (
  name: string,
  data: Record<string, unknown>
) => {
  Sentry.setContext(name, data)
}

export const addSentryBreadcrumb = (
  message: string,
  category: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = "info"
) => {
  Sentry.addBreadcrumb({ message, category, data, level })
}

export const captureException = (
  error: Error | unknown,
  context?: Record<string, unknown>
) => {
  if (context) {
    Sentry.withScope((scope) => {
      scope.setExtras(context)
      Sentry.captureException(error)
    })
  } else {
    Sentry.captureException(error)
  }
}

export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = "info",
  context?: Record<string, unknown>,
  tags?: Record<string, string>
) => {
  if (context || tags) {
    Sentry.withScope((scope) => {
      if (context) scope.setExtras(context)
      if (tags) scope.setTags(tags)
      Sentry.captureMessage(message, level)
    })
  } else {
    Sentry.captureMessage(message, level)
  }
}

export { ErrorBoundary as SentryErrorBoundary } from "@sentry/react"
